import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import { WatchlistData } from "./get-active-watchlist";

interface RequestParams {
  token: string;
  watchlistId: string;
  lang: Lang;
}

export const responseDataSchema = z.object({
  message: z.string(),
});

export const deleteWatchlist = async ({
  token,
  watchlistId,
  lang,
}: RequestParams): Promise<z.infer<typeof responseDataSchema>> => {
  const endpoint = `/watchlist/${watchlistId}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-User-Profile": token,
      "Accept-Language": lang,
    },
    body: JSON.stringify({ watchlistId }),
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseDataSchema.parse(await res.json());
};

export const useDeleteWatchlist = () => {
  const { backendToken: token } = useAuth();
  const queryClient = useQueryClient();
  const { lang } = useLang();
  return useMutation(
    (params: Omit<RequestParams, "token" | "lang">) =>
      deleteWatchlist({ token: token ?? "", lang, ...params }),
    {
      onMutate: async ({ watchlistId }) => {
        await queryClient.cancelQueries(["get-all-watchlists", token, lang]);
        await queryClient.cancelQueries(["get-active-watchlist", token, lang]);

        const previousWatchlist = queryClient.getQueryData<WatchlistData>([
          "get-active-watchlist",
          token,
          lang,
        ]);

        const previousWatchlists = queryClient.getQueryData<WatchlistData[]>([
          "get-all-watchlists",
          token,
          lang,
        ]);

        if (previousWatchlist?.watchlistId === watchlistId) {
          queryClient.setQueryData<WatchlistData | undefined>(
            ["get-active-watchlist", token, lang],
            () => previousWatchlists?.[0]
          );
        }

        queryClient.setQueryData<WatchlistData[] | undefined>(
          ["get-all-watchlists", token, lang],
          (old) => old && old.filter((w) => w.watchlistId !== watchlistId)
        );

        return { previousWatchlist, previousWatchlists };
      },
      onError: (err, variables, context) => {
        queryClient.setQueryData(
          ["get-active-watchlist", token, lang],
          context?.previousWatchlist
        );
        queryClient.setQueryData(
          ["get-all-watchlists", token, lang],
          context?.previousWatchlists
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-active-watchlist", token, lang],
        });
        queryClient.invalidateQueries({
          queryKey: ["get-all-watchlists", token, lang],
        });
      },
    }
  );
};
